/* eslint-disable import/no-unresolved */
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import heroImage from '../../images/luxury-car-inspection-hero.jpg';

export default function LuxuryCarInspectionPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Why do luxury cars need specialized inspection?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Luxury cars like BMW, Mercedes-Benz, and Audi have complex electronics, air suspension systems, and advanced safety features that require specialized knowledge to inspect properly, and repair costs are significantly higher if issues are missed.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which luxury car brands do you inspect?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We inspect all major luxury and premium brands including BMW, Mercedes-Benz, Audi, Range Rover, Land Rover, Volvo, and Jaguar.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is the premium inspection package recommended for luxury cars?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, we recommend our Premium Inspection package for luxury and high-value cars, as it includes extended diagnostics suited to complex electronic and mechanical systems.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does luxury car inspection cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Normal Inspection is Rs.4,999 and Premium Inspection is Rs.7,999, with Premium being the recommended choice for luxury vehicles.',
        },
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Luxury Car Inspection',
    provider: { '@type': 'Organization', name: 'Focus Motors', url: 'https://focusmotors.in' },
    areaServed: { '@type': 'State', name: 'Kerala' },
    url: 'https://focusmotors.in/luxury-car-inspection-kerala',
    description:
      'Specialized luxury car inspection in Kerala for BMW, Mercedes-Benz, Audi, Range Rover and other premium brands. 130+ point checks with focus on complex electronics and systems.',
    offers: [
      {
        '@type': 'Offer',
        name: 'Normal Inspection',
        price: '4999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
      {
        '@type': 'Offer',
        name: 'Premium Inspection',
        price: '7999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Luxury Car Inspection in Kerala | Focus Motors</title>
        <meta
          name="description"
          content="Specialized luxury car inspection in Kerala for BMW, Mercedes-Benz, Audi, Range Rover and more. 130+ point checks. Normal Rs.4,999 | Premium Rs.7,999."
        />
        <meta
          name="keywords"
          content="luxury car inspection Kerala, BMW inspection Kerala, Mercedes inspection Kerala, Audi inspection Kerala, premium car inspection"
        />
        <link rel="canonical" href="https://focusmotors.in/inspection" />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Luxury Car Inspection in Kerala | Focus Motors" />
        <meta
          property="og:description"
          content="Specialized inspection for BMW, Mercedes-Benz, Audi, Range Rover and other luxury brands across Kerala."
        />
        <meta property="og:url" content="https://focusmotors.in/luxury-car-inspection-kerala" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div
        style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: 'inherit', color: '#ffffff' }}
      >
        <img
          src={heroImage}
          alt="Luxury car inspection service in Kerala"
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '450px',
            objectFit: 'cover',
            borderRadius: '12px',
            marginBottom: '30px',
          }}
        />

        <h1>Luxury Car Inspection in Kerala</h1>
        <p>
          Buying or maintaining a luxury car? Complex electronics, air suspension, and advanced safety systems mean
          luxury cars need specialized inspection. Focus Motors provides expert luxury car inspection across Kerala.
        </p>
        <p>
          For luxury vehicles, our{' '}
          <Link to="/inspection">Pre-Purchase Car Inspection</Link> service includes specialized checks.
        </p>
        <h2>Why Luxury Cars Need Specialized Inspection</h2>
        <ul>
          <li>
            <strong>Complex electronics</strong> — modern luxury cars have advanced computer systems that require
            specialized diagnostic tools
          </li>
          <li>
            <strong>Higher repair costs</strong> — missed issues can be far more expensive to fix than on ordinary cars
          </li>
          <li>
            <strong>Air suspension and advanced systems</strong> — components unique to luxury vehicles need expert
            evaluation
          </li>
          <li>
            <strong>Resale value protection</strong> — thorough inspection helps preserve the car's value over time
          </li>
        </ul>

        <h2>Brands We Inspect</h2>
        <p>
          BMW, Mercedes-Benz, Audi, Range Rover, Land Rover, Volvo, Jaguar, and other premium and luxury vehicle brands.
        </p>

        <h2>Recommended Package for Luxury Cars</h2>
        <p>
          <strong>Premium Inspection — Rs.7,999</strong>
          <br />
          For premium and luxury cars. Same 130+ point inspection, with extra care given to complex electronics and
          specialized systems.
        </p>
        <p>
          <strong>Normal Inspection — Rs.4,999</strong>
          <br />
          For normal cars. Complete 130+ point inspection.
        </p>
        <a
          href="https://focusmotors.in/inspection-booking"
          style={{
            display: 'inline-block',
            marginTop: '10px',
            padding: '12px 24px',
            background: '#000',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '6px',
          }}
        >
          Book Luxury Car Inspection
        </a>

        <h2>Available Across Kerala</h2>
        <p>
          Our luxury car inspection service covers all of Kerala — Kochi, Thrissur, Kozhikode, Thiruvananthapuram, and
          more.
        </p>

        <h2>Frequently Asked Questions</h2>

        <h3>Why do luxury cars need specialized inspection?</h3>
        <p>
          Luxury cars have complex electronics, air suspension, and advanced safety features that require specialized
          knowledge, and repair costs are significantly higher if issues are missed.
        </p>

        <h3>Which luxury car brands do you inspect?</h3>
        <p>We inspect BMW, Mercedes-Benz, Audi, Range Rover, Land Rover, Volvo, and Jaguar.</p>

        <h3>Is the premium inspection package recommended for luxury cars?</h3>
        <p>Yes, we recommend our Premium Inspection package for luxury and high-value cars.</p>

        <h3>How much does luxury car inspection cost?</h3>
        <p>Normal Inspection is Rs.4,999 for normal cars and Premium Inspection is Rs.7,999 for premium/luxury cars.</p>
      </div>
    </>
  );
}
